// src/pages/Weddings/OrderConfirmationPage.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Chip,
  Alert,
} from "@mui/material";
import api from "../../services/api";

/* ----------------------------- helpers ----------------------------- */
const fmtMoney = (n, c = "CAD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: c }).format(
    Number(n || 0)
  );

const fmtDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
};

const isDepositLine = (line) => {
  if (line?.kind === "deposit") return true;
  const refId = String(line?.ref?.id || "");
  return refId.startsWith("deposit:");
};

const isServiceLine = (line) =>
  line?.kind === "service" || line?.itemType === "fee";
const isRentalLine = (line) => line?.itemType === "rental";
const isDessertLine = (line) => line?.itemType === "dessert";
const isCakeLine = (line) => line?.itemType === "cake";

const lineRefundable = (line) => {
  if (typeof line?.refundable === "boolean") return line.refundable;
  return isDepositLine(line);
};

// Trim “, Canada” from the end to shorten long strings a bit
const formatAddress = (addr = {}) => {
  const s = String(addr.line1 || "")
    .replace(/,\s*Canada\s*$/i, "")
    .trim();
  return s || "We’ll confirm by email";
};

/* ------------------------------ UI ------------------------------- */
// Grid-based row: keeps a fixed label column + a flexible value column that can wrap
function Row({ left, right, strong, muted, wrap = false }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "120px 1fr", sm: "160px 1fr" },
        alignItems: "start",
        columnGap: 1.25,
        rowGap: 0.25,
        opacity: muted ? 0.75 : 1,
        mb: 0.25,
      }}>
      <Typography
        variant={strong ? "body1" : "body2"}
        sx={{ fontWeight: strong ? 700 : 600 }}>
        {left}
      </Typography>
      <Typography
        variant={strong ? "body1" : "body2"}
        sx={{
          fontWeight: strong ? 700 : 400,
          whiteSpace: wrap ? "normal" : "nowrap",
          overflow: wrap ? "visible" : "hidden",
          textOverflow: wrap ? "clip" : "ellipsis",
          wordBreak: "break-word",
        }}>
        {right}
      </Typography>
    </Box>
  );
}

function SectionCard({ title, actions, children }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent sx={{ p: 2.25 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {actions}
        </Stack>
        <Box sx={{ mt: 1 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

export default function OrderConfirmationPage() {
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const id = new URLSearchParams(window.location.search).get("order");

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const { data } = await api.get(`/weddings/orders/${id}`);
        if (ok) setOrder(data?.order || null);
      } catch {
        if (ok) setOrder(null);
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 6, display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 6 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          We’re checking your order…
        </Typography>
        <Typography color="text.secondary">
          We couldn’t find that order right now. If you paid, don’t worry—your
          receipt will arrive by email.
        </Typography>
      </Box>
    );
  }

  const currency = order.currency || order.payment?.currency || "CAD";
  const shortId = order.shortId || order._id;
  const method =
    order.fulfillment?.method === "delivery" ? "Delivery" : "Pickup";
  const eventDate = fmtDate(order.fulfillment?.scheduledDate);
  const pickupLocation = order.fulfillment?.pickupLocation || "";
  const address = order.fulfillment?.address || {};
  const setupSelected = !!order.fulfillment?.setupSelected;

  const pay = order.payment || {};
  const amountPaid = Number(pay.amountCaptured || 0);
  const outstanding = Number(pay.outstanding || 0);
  const paymentStatus = pay.status || "unpaid";
  const depositPolicy = pay.depositPolicySnapshot || {};
  const savedForAutoCharge = !!pay.stripeCustomerId && outstanding > 0;

  // Totals
  const pricing = order.pricing || {};
  const lines = Array.isArray(pricing.lines) ? pricing.lines : [];
  const total = Number(pricing.total || 0);
  const depositsTotal = lines
    .filter(isDepositLine)
    .reduce((s, l) => s + Number(l.lineTotalInclTax || l.lineSubtotal || 0), 0);
  const itemsTotal = total - depositsTotal;

  // Group lines for display
  const groups = {
    cake: lines.filter(isCakeLine),
    desserts: lines.filter(isDessertLine),
    rentals: lines.filter(isRentalLine),
    services: lines.filter((l) => isServiceLine(l) && !isDepositLine(l)),
    deposits: lines.filter(isDepositLine),
  };

  return (
    <Box sx={{ p: { xs: 3, md: 6 }, maxWidth: 1000, mx: "auto" }}>
      {/* Header */}
      <Typography variant="h5" sx={{ fontWeight: 800 }}>
        Order confirmation
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5 }}>
        Order <strong>{shortId}</strong> • Event on{" "}
        <strong>{eventDate || "TBD"}</strong>
      </Typography>

      {/* Status */}
      <Box sx={{ mt: 2, mb: 2 }}>
        {paymentStatus === "paid" ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            Payment received — thank you!
          </Alert>
        ) : paymentStatus === "authorized" ||
          paymentStatus === "partially_paid" ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            We’ve received your payment. Your remaining balance is{" "}
            {fmtMoney(outstanding, currency)}.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Order received — payment pending.
          </Alert>
        )}
      </Box>

      {/* Event & contact */}
      <SectionCard title="Event & Contact">
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Row left="Fulfillment" right={method} />
            {method === "Pickup" ? (
              <Row
                left="Pickup location"
                right={pickupLocation || "We’ll confirm by email"}
                wrap
              />
            ) : (
              <Row left="Address" right={formatAddress(address)} wrap />
            )}
            {setupSelected && <Row left="Setup service" right="Included" />}
            <Row left="Event date" right={eventDate || "TBD"} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Row
              left="Name"
              right={`${order.customer?.firstName || ""} ${
                order.customer?.lastName || ""
              }`.trim()}
            />
            <Row left="Email" right={order.customer?.email || "-"} wrap />
            <Row left="Phone" right={order.customer?.phone || "-"} />
          </Grid>
        </Grid>
      </SectionCard>

      {/* Items */}
      {groups.cake.length > 0 && (
        <SectionCard title="Wedding cake">
          {groups.cake.map((l) => (
            <LineRow key={l.lineId} line={l} currency={currency} />
          ))}
        </SectionCard>
      )}

      {groups.desserts.length > 0 && (
        <SectionCard title="Desserts">
          {groups.desserts.map((l) => (
            <LineRow key={l.lineId} line={l} currency={currency} />
          ))}
        </SectionCard>
      )}

      {groups.rentals.length > 0 && (
        <SectionCard title="Rentals">
          {groups.rentals.map((l) => (
            <LineRow key={l.lineId} line={l} currency={currency} />
          ))}
        </SectionCard>
      )}

      {groups.services.length > 0 && (
        <SectionCard title="Services & fees">
          {groups.services.map((l) => (
            <LineRow key={l.lineId} line={l} currency={currency} />
          ))}
        </SectionCard>
      )}

      {groups.deposits.length > 0 && (
        <SectionCard
          title="Refundable deposits"
          actions={
            <Chip
              size="small"
              label={`Total ${fmtMoney(depositsTotal, currency)}`}
            />
          }>
          {groups.deposits.map((l) => (
            <LineRow
              key={l.lineId}
              line={l}
              currency={currency}
              showRefundable
            />
          ))}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}>
            Deposits are refunded after rental items are returned in good
            condition.
          </Typography>
        </SectionCard>
      )}

      {/* Totals & payment plan */}
      <SectionCard title="Payment summary">
        <Row left="Order total" right={fmtMoney(total, currency)} strong />
        {depositsTotal > 0 && (
          <Row
            left="Includes refundable deposits"
            right={fmtMoney(depositsTotal, currency)}
            muted
          />
        )}
        <Row
          left="Non-refundable subtotal"
          right={fmtMoney(itemsTotal, currency)}
          muted
        />

        <Divider sx={{ my: 1 }} />

        <Row left="Paid now" right={fmtMoney(amountPaid, currency)} strong />
        {outstanding > 0 && (
          <Row
            left="Balance due"
            right={fmtMoney(outstanding, currency)}
            strong
          />
        )}

        {outstanding > 0 && depositPolicy?.type === "percent" && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You selected a {depositPolicy.value}% deposit on non-refundable
            items.
            {savedForAutoCharge
              ? " Your remaining balance will be automatically charged 7 days before the event."
              : " The remaining balance is due 7 days before the event."}
          </Typography>
        )}
      </SectionCard>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        A detailed receipt has been emailed to{" "}
        {order.customer?.email || "your address"}. If any details look off,
        reply to that email and we’ll help right away.
      </Typography>
    </Box>
  );
}

/* ---------- Single line display with refundable badge when relevant ---------- */
function LineRow({ line, currency, showRefundable = false }) {
  const qty = Number(line?.qty || 0);
  const unit = line?.unit || "each";
  const unitPrice = Number(line?.unitPrice || 0);
  const total = Number(line?.lineTotalInclTax ?? line?.lineSubtotal ?? 0);

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={0.5}
        sx={{ py: 0.5 }}>
        <Box sx={{ pr: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {line?.description || line?.ref?.id || "Item"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {qty} × {fmtMoney(unitPrice, currency)} / {unit}
          </Typography>
          {showRefundable && lineRefundable(line) && (
            <Chip
              size="small"
              label="Refundable"
              sx={{ ml: 1, height: 20, fontSize: 12 }}
              color="success"
              variant="outlined"
            />
          )}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {fmtMoney(total, currency)}
        </Typography>
      </Stack>
      <Divider sx={{ my: 0.75 }} />
    </>
  );
}
